<template>
  <div
    class="flex-row flex-grow position-relative h-full"
    data-test="producer-timeline"
  >
    <div v-if="!isSocketConnected" class="disconnected-overlay" />

    <div class="playlist level-01dp">
      <div class="header panel-drag-handle">
        <div class="playlist-info">
          <span class="title margin-right-sm">Playlist</span>
        </div>

        <div class="playlist-settings panel-drag-exclude">
          <button
            class="tb-btn tb-btn-contained medium-em margin-right-sm sm"
            data-entity="producer-timeline-addGroup"
            @click="addGraphicsGroup"
          >
            Add group
          </button>

          <div class="vertical-divider"></div>

          <div class="playlist-resizer margin-right-sm margin-left-sm">
            <span class="icon icon-picture icon-left margin-right-xxs"></span>
            <input class="tb-range" v-model="zoom" type="range" min="1" max="5" step="1">
            <span class="icon icon-picture icon-right margin-left-xxs"></span>
          </div>

          <div class="vertical-divider"></div>

          <button
            id="timelineSettingsButton"
            title="Timeline settings"
            class="tb-btn tb-btn-link sm low-em margin-left-xs"
            data-entity="producer-timeline-openSettings"
            @click="showSettingsModal = true"
          >
            <span class="fas fa-cog" />
          </button>
        </div>
      </div>

      <div class="body">
        <div v-if="!layouts.length" class="playlist-droparea">
          <div class="droparea">
            <div class="droparea-help-text">
              Drag + drop Social Displays, Interactive Displays, or Graphics here
            </div>
          </div>
        </div>

        <draggable
          v-model="layouts"
          @change="onChange"
          @start="startDrag"
          @end="endDrag"
          class="playlist-draggable draggable"
          :class="[{'empty': !this.layouts.length},'zoom-' + zoom]"
          group="timeline"
          :animation="200"
          revert-on-spill="true"
          handle=".drag-handle"
          data-test="producer-timeline-droparea"
        >
          <component
            v-for="(item, index) in layouts"
            :key="item.timeline_id"
            :is="getType(item)"
            :index="index"
            :item="item"
            :prev="index > 0 ? layouts[index - 1] : null"
            :next="index < layouts.length - 1 ? layouts[index + 1] : null"
            :zoom="zoom"
          />
        </draggable>
      </div>

      <div class="footer playlist-toolbar">
        <div class="playlist-actions left">
          <button
            :data-balloon="timelineMuted ? 'Unmute' : 'Mute'"
            data-balloon-pos="up-left"
            class="tb-btn tb-btn-text sm medium-em"
            :class="timelineMuted ? 'active' : ''"
            data-entity="producer-timeline-toggleMute"
            @click="toggleMute"
          >
            <span class="icon" :class="`icon-volume-${timelineMuted ? 'off' : 'up'}`" />
          </button>

          <button
            :data-balloon="timelineLooped ? 'Disable Loop' : 'Enable Loop'"
            data-balloon-pos="up-left"
            class="tb-btn tb-btn-text sm medium-em"
            :class="timelineLooped ? 'active' : ''"
            data-entity="producer-timeline-toggleRepeat"
            @click="toggleLoop"
          >
            <span class="icon icon-repeat" />
          </button>
        </div>

        <div class="playlist-actions middle">
          <button
            class="tb-btn tb-btn-text sm medium-em"
            data-balloon="Go Back"
            data-balloon-pos="up"
            :disabled="!hasActiveItem"
            data-entity="producer-timeline-goBack"
            @click="advance('previous')"
          >
            <span class="icon icon-backward" />
          </button>

          <button
            class="tb-btn tb-btn-text sm medium-em"
            :class="{ 'active': playing }"
            :disabled="!hasActiveItem"
            data-balloon="Toggle Auto-Advance"
            data-balloon-pos="up"
            data-entity="producer-timeline-togglePlaying"
            @click="togglePlaying"
          >
            <span
              class="icon"
              :class="`icon-${playing ? 'pause' : 'play'}`"
            />
          </button>

          <button
            class="tb-btn tb-btn-text sm medium-em"
            data-balloon="Go Forward"
            data-balloon-pos="up"
            :disabled="!hasActiveItem"
            data-entity="producer-timeline-goForward"
            @click="advance('next')"
          >
            <span class="icon icon-forward" />
          </button>
        </div>

        <div class="playlist-actions right">
          <template v-if="showDuration">
            <span v-if="duration" class="countdown margin-right-md">
              {{countdown | toMinutesAndSeconds}} / {{duration | toMinutesAndSeconds}}
            </span>
          </template>

          <button
            class="tb-btn tb-btn-text sm medium-em"
            :disabled="!layouts || !layouts.length"
            data-entity="producer-timeline-clearPlaylist"
            @click="confirm"
          >
            Clear
          </button>
        </div>

      </div>

      <alert
        v-if="showAlert"
        footerConfirm="true"
        @confirm="clearTimeline"
        @close="showAlert = false"
      >
        <h2>Are you sure?</h2>
        <p>This will clear out everything in the playlist.</p>
      </alert>
    </div>

    <modal
      v-if="showSettingsModal"
      class="producer-settings-modal"
      @close="showSettingsModal = false"
    >
      <template slot="modal-header">
        <div class="title">Production Options</div>
        <button
          class="modal-close tb-btn tb-btn-text sm low-em"
          data-entity="producer-timeline-closeSettings"
          @click="showSettingsModal = false"
        >
          <span class="icon icon-cross" />
        </button>
      </template>

      <template slot="modal-body">
        <div class="producer-settings__field">
          <div class="producer-settings__label">
            Graphic Transition Mode
          </div>

          <div class="producer-settings__description">
            This controls how graphics should animate in and out
            when moving between them on the timeline.
            Out/In waits for the current graphics to leave before showing the new ones (default).
            Overlap will start showing the new graphics right away.
          </div>

          <select
            class="tb-select sm"
            v-model="transitionMode"
            title="Transition mode used when switching between graphics"
            data-entity="producer-timeline-transitionMode"
            @change="setTransitionMode"
          >
            <option value="out-in">Out/In</option>
            <option value="overlap">Overlap</option>
          </select>
        </div>

        <hr />

        <div class="producer-settings__field">
          <div class="producer-settings__label">
            Social Display Transition Mode
          </div>

          <div class="producer-settings__description">
            This controls how themes should animate in and out
            when moving between them on the timeline.
            Cut will immediately hide the current theme and switch to the new one.
            Fade will slowly fade in the new theme over the current one.
          </div>

          <select
            class="tb-select sm"
            v-model="transition"
            title="Transition mode used when switching between themes"
            data-entity="producer-timeline-transition"
            @change="setTransition"
          >
            <option value="animated cutIn">Cut</option>
            <option value="animated fadeIn">Fade</option>
          </select>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import draggable from 'vuedraggable';

import Alert from '../alert/Alert.vue';
import Modal from '../modal/Modal.vue';

import ConduitEventsService from '../../services/conduitEventsService';

import TimelinePanelComponent from './panel/TimelinePanel.vue';
import TimelineGraphicComponent from './graphic/TimelineGraphic.vue';
import TimelineGraphicsComponent from './graphic/TimelineGraphics.vue';
import TimelineMultipostComponent from './multipost/TimelineMultipost.vue';
import TimelineThemeComponent from './theme/TimelineTheme.vue';
import TimelineAsset from './TimelineAsset.vue';
import TimelinePoll from './TimelinePoll.vue';

const dispatchEvent = (event) => {
  const evt = document.createEvent('Event');
  evt.initEvent(event, true, true);
  document.dispatchEvent(evt);
};

export default {
  name: 'Timeline',
  components: {
    draggable,
    Alert,
    Modal,

    theme: TimelineThemeComponent,
    graphic: TimelineGraphicComponent,
    graphics: TimelineGraphicsComponent,
    panel: TimelinePanelComponent,
    multi: TimelineMultipostComponent,
    asset: TimelineAsset,
    poll: TimelinePoll,
  },

  data() {
    return {
      paused: true,
      interval: null,
      countdown: 0,
      showAlert: false,
      zoom: localStorage.getItem('slider') || 1,
      showSettingsModal: false,
    };
  },

  mounted() {
    if ((this.playing || this.videoPlaying) && this.duration) {
      // Start the timer on the timeline if auto-advancing OR video is playing
      this.startCountdown();
    }
  },

  watch: {
    sub_index(subIndex, oldSubIndex) {
      if (subIndex !== oldSubIndex) {
        this.countdown = 0;

        // Check if the new post is a video, since that can determine if we should keep the timer rolling
        if (this.playing) {
          this.startCountdown();
        } else {
          this.stopCountdown();
        }
      }
    },

    zoom() {
      localStorage.setItem('slider', this.zoom);
    },

    videoPlaying(videoPlaying) {
      if (videoPlaying) {
        this.startCountdown();
      } else {
        this.stopCountdown();
      }
    },

    playing(playing) {
      if (!playing) {
        this.stopCountdown();
      }
    },

    duration(val) {
      if (!val || !this.paused) {
        return;
      }

      // If duration changed, and something is playing but the timer is paused...
      if (this.playing) {
        // ... then start that ish
        this.startCountdown();
      }
    },
  },
  computed: {
    ...mapGetters([
      'getLiveSocialPost',
      'getLiveTagboard',
    ]),

    ...mapState({
      isSocketConnected: (state) => state.clients.isSocketConnected,
    }),

    showDuration() {
      return this.hasActiveItem && this.playing;
    },

    // Used for graphic transitions
    transitionMode: {
      get() {
        return this.production.transitionMode || 'out-in';
      },

      set(transitionMode) {
        this.$set(this.production, 'transitionMode', transitionMode);
      },
    },

    // Used for theme transitions
    transition: {
      get() {
        return this.production.transition || 'animated cutIn';
      },

      set(transition) {
        this.$set(this.production, 'transition', transition);
      },
    },

    production() {
      return this.$store.state.production.production;
    },

    sub_index() {
      return this.production.sub_index;
    },

    layouts: {
      get() {
        return this.production.timeline;
      },
      set(layouts) {
        this.$store.dispatch('setLayouts', layouts);
      },
    },

    videoPlaying() {
      return this.$store.state.production.production.videoPlaying;
    },

    playing() {
      return this.$store.state.production.production.playing;
    },

    timelineMuted() {
      return this.$store.state.production.production.muted;
    },

    timelineLooped() {
      return this.$store.state.production.production.looped;
    },

    hasActiveItem() {
      const { index } = this.production;
      return !!index;
    },

    duration() {
      return this.$store.state.production.duration;
    },
  },
  methods: {
    addGraphicsGroup() {
      this.$store.dispatch('addGraphicsGroup');

      ConduitEventsService.track(
          'producer.created.group-from-playlist-button',
          { production_id: this.production._id },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
           },
      );
    },

    startCountdown() {
      this.paused = false;

      clearInterval(this.interval);

      let prevTime = Date.now();

      this.interval = window.setInterval(() => {
        if (this.paused) { return; }

        // Get exact amount of time that has passed,
        // jic the interval has been throttled by the browser
        this.countdown += Date.now() - prevTime;
        prevTime = Date.now();

        if (this.countdown >= this.duration) {
          // Reset to zero, but keep the countdown running.
          // That way it'll loop or keep going for the next post.
          this.countdown = 0;
        }
      }, 100);
    },

    stopCountdown() {
      this.paused = true;
    },

    setTransitionMode(e) {
      this.$store.dispatch('sendSettings');
      this.$store.dispatch('sendPreviewSettings');

      ConduitEventsService.track(
          'producer.changed.transition-mode',
          {
            transitionMode: e.target.value,
            ownerId: this.production.owner,
            production_id: this.production._id,
          },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    setTransition(e) {
      this.$store.dispatch('sendSettings');
      this.$store.dispatch('sendPreviewSettings');

      ConduitEventsService.track(
          'producer.changed.transition',
          {
            transition: e.target.value,
            ownerId: this.production.owner,
            production_id: this.production._id,
          },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    togglePlaying() {
      this.$store.dispatch(this.playing ? 'pauseTimeline' : 'playTimeline');
    },

    toggleMute() {
      this.$store.dispatch('toggleMute');
    },

    toggleLoop() {
      this.$store.dispatch('toggleLoop');
    },

    confirm() {
      this.showAlert = true;
    },

    clearTimeline() {
      this.$store.dispatch('clearLayerEditing');
      this.$store.dispatch('clearTimeline');
      this.$store.dispatch('clearPreview');
      this.showAlert = false;
    },

    startDrag(e) {
      // Since the timeline is filled with different components,
      // we need to check the actual type set on the element here
      const type = e.item.dataset.componentType;
      this.$store.dispatch('setDragType', type || 'component');
    },

    endDrag() {
      this.$store.dispatch('clearDragType');
    },

    async onChange(e) {
      // Type not added, so don't log any events
      if (!e.added) {
        return;
      }

      // This should be handled with global state,
      // but we'll just let the tabs know to clear for now
      dispatchEvent('clearTabSelection');

      const { element, newIndex } = e.added;

      const { layout, type } = element;

      const { group } = this.$store.state.drag;

      ConduitEventsService.track(
        'producer.added.items-to-playlist',
        {
          type,
          count: group?.length || 1,
          production_id: this.production._id,
          component: 'Timeline.vue',
          method: 'onChange',
          items: group,
        },
        {
          ownerId: this.production.owner,
          userId: this.$store.state.user.id,
          isStaff: this.$store.getters.isStaff,
        },
      );

      if (group && group.length) {
        // Add entire group to playlist
        this.$store.dispatch('replacePlaylistItem', {
          index: newIndex,
          items: group,
        });

        await Promise.all(
          group.map(async (item) => {
            if (item.layout === 'panel') {
              // If user added a panel, make sure we load the settings for it
              await this.$store.dispatch('getTimelinePanels');
            } else if (item.layout === 'graphic') {
              await this.$store.dispatch('getTimelineTemplates');
            } else if (item.layout === 'asset') {
              await this.$store.dispatch('assets/getTimelineAssets');
            } else if (item.layout === 'poll') {
              await this.$store.dispatch('polls/getTimelinePolls');
            } else {
              await this.$store.dispatch('getTimelineThemes');
            }
          }),
        );

        await this.$store.dispatch('generateShortUrls', group.map((item) => item.timeline_id));
        this.$store.dispatch('updateTimeline');
        this.$store.dispatch('trackSportradarInGameElement', group.map((item) => item.timeline_id));

        return;
      }

      if (layout === 'panel') {
        // If user added a panel, make sure we load the settings for it
        await this.$store.dispatch('getTimelinePanels');
      } else if (layout === 'graphic') {
        await this.$store.dispatch('getTimelineTemplates');
      } else if (layout === 'asset') {
        await this.$store.dispatch('assets/getTimelineAssets');
      } else if (layout === 'poll') {
        await this.$store.dispatch('polls/getTimelinePolls');
      } else {
        await this.$store.dispatch('getTimelineThemes');
      }

      await this.$store.dispatch('generateShortUrls', [element.timeline_id]);
      this.$store.dispatch('updateTimeline');
      this.$store.dispatch('trackSportradarInGameElement', [element.timeline_id]);
    },

    advance(direction) {
      this.$store.dispatch('advance', direction);
    },

    getType(item) {
      const { layout } = item;

      if (['panel', 'graphic', 'graphics', 'asset', 'poll'].includes(layout)) {
        // Just return the layout directly
        return item.layout;
      }

      // Timeline item is multipost if it's a specific type of theme
      // or if it contains a tagboard as it's first post
      const isMultiPostLayout = (
        ['grid', 'waterfall', 'carousel', 'mural', 'upnext'].includes(layout)
        || (item.posts?.[0]?.type === 'tagboard')
      );

      // If it's not a multipost element, then we'll call it a theme
      return isMultiPostLayout ? 'multi' : 'theme';
    },
  },
};
</script>
